import React, { useEffect, useState } from 'react'
import useGroup from './useGroup'
import { usePlace } from 'commons/usePlace'
import PanelPages from 'app/componentV4/Panels/Pages'
import DataTable from 'app/componentV4/Table/DataTable'
import { isEmpty, isNil, mergeLeft } from 'ramda'
import EmptyState from 'app/componentV4/EmptyState'
import { useIntl } from 'react-intl'
import { AccessType } from 'app/auth/accessControlService'
import {
  FormControl,
  InputAdornment,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import SearchIcon from '@material-ui/icons/Search'
import Assets from 'app/configs/assetConfigs'
import { SECONDARY } from 'app/constants/button'
import { GROUP_MAX } from 'app/constants/group'
import AccessControl from 'app/auth/AccessControl'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import { GroupIcon } from 'app/components/icons'
import ComposeMessageButton from 'app/componentV4/Button/ComposeMessageButton'
import { ColumnOfData } from './ColumnOfData'
import { useAccessControl } from 'app/auth/useAccessControl'
import { Routes } from 'app/services/routeService/routeService'
import { useGroupDialog } from './contexts/GroupDialogContext'
import { isEmptyOrNil } from 'libs/empty'
import { DIALOG_KEYS } from './constants'
import clsx from 'clsx'
import { useIsRootAdmin, useUserProfile } from 'commons/useUserProfile'

const useStyle = makeStyles((theme) => ({
  grouptable: {
    height: '85vh',
  },
  table: {
    '&.data-table': {
      [theme.breakpoints.up('md')]: {
        height: 'calc(100vh - 150px)',
      },
    },
  },
}))

export default (props) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyle()
  const [isResetSelected, setIsResetSelected] = useState(false)
  const {
    state: {
      groups,
      groupCount,
      isLoading,
      meta,
      searchText,
      params,
      isEmptyGroups,
    },
    refetchGroups,
    getGroupFirstTime,
    createGroups,
    deleteGroupByIds,
    searchGroups,
    openCompose,
    editGroupName,
    addSubgroup,
    getTagIds,
  } = useGroup()

  const { openDialog, setLoading, closeDialog } = useGroupDialog()

  const place = usePlace()
  const [selectedIds, setSelectedIds] = useState({})
  const [isComposeLoading, setIsComposeLoading] = useState(false)
  const { isGranted } = useAccessControl([
    AccessType.ADMINISTRATION,
    AccessType.SUBSCRIBER_VIEW,
  ])
  // const isRootAdmin = useIsRootAdmin()
  const userData = useUserProfile()
  const isOwner = userData.places[0].roles[0].type === 2

  const isLastPage = meta.currentPage === meta.lastPage
  // const isOwner = isRootAdmin(userData.id)
  const goToGroupDetail = ({ groupId }) => {
    if (isGranted) {
      props.history.push(Routes.viewGroupDetail(groupId))
    }
  }

  const onClearData = () => {
    setSelectedIds({})
    setIsResetSelected(true)
  }

  const onOpenAddGroupDialog = () => {
    openDialog(DIALOG_KEYS.GROUP_ADD, {
      onSubmit: ({ data, resetData, setFieldError }) => {
        setLoading(DIALOG_KEYS.GROUP_ADD, true)
        createGroups(data)
          .then((res) => {
            setLoading(DIALOG_KEYS.GROUP_ADD, false)
            closeDialog(DIALOG_KEYS.GROUP_ADD)
            resetData()
            refetchGroups({ placeId: place.id })
          })
          .catch((error) => {
            const code = error?.response?.data?.code
            if (code === 608) {
              setFieldError(
                'groupName',
                formatMessage({
                  id: 'duplicate.group.name.error.message',
                }),
              )
            }
          })
          .finally(() => {
            setLoading(DIALOG_KEYS.GROUP_ADD, false)
          })
      },
    })
  }

  const onOpenDeleteGroupDialog = (groupIds, type) => {
    openDialog(DIALOG_KEYS.GROUP_DELETE, {
      type,
      onSubmit: () => {
        setLoading(DIALOG_KEYS.GROUP_DELETE, true)
        deleteGroupByIds({
          groupIds,
          placeId: place.id,
        }).then((res) => {
          setLoading(DIALOG_KEYS.GROUP_DELETE, false)
          closeDialog(DIALOG_KEYS.GROUP_DELETE)
          onClearData()
          let newParams = mergeLeft({ placeId: place.id }, params)
          const countOnLastPage =
            meta?.total % meta?.perPage === 0
              ? meta?.perPage
              : meta?.total % meta?.perPage
          if (isLastPage && countOnLastPage === groupIds.length) {
            newParams = mergeLeft(
              { placeId: place.id, page: meta?.lastPage - 1 },
              params,
            )
          }

          refetchGroups(newParams)
        })
      },
    })
  }

  const openEditNameGroupDialog = (group) => {
    openDialog(DIALOG_KEYS.GROUP_EDIT_NAME, {
      group,
      onSubmit: ({ data, setFieldError }) => {
        setLoading(DIALOG_KEYS.GROUP_EDIT_NAME, true)
        editGroupName(data)
          .then((res) => {
            setLoading(DIALOG_KEYS.GROUP_EDIT_NAME, false)
            closeDialog(DIALOG_KEYS.GROUP_EDIT_NAME)
            refetchGroups({ placeId: place.id })
          })
          .catch((error) => {
            const code = error?.response?.data?.details?.code
            if (code === 608) {
              setFieldError(
                'name',
                formatMessage({ id: 'error.400.groupNameExists' }),
              )
            }
          })
          .finally(() => {
            setLoading(DIALOG_KEYS.GROUP_EDIT_NAME, false)
          })
      },
    })
  }

  const openAddSubgroupDialog = (group) => {
    openDialog(DIALOG_KEYS.SUB_GROUP_ADD, {
      group,
      onSubmit: ({ data, resetData, setFieldError }) => {
        setLoading(DIALOG_KEYS.SUB_GROUP_ADD, true)
        addSubgroup(data)
          .then((res) => {
            setLoading(DIALOG_KEYS.SUB_GROUP_ADD, false)
            closeDialog(DIALOG_KEYS.SUB_GROUP_ADD)
            resetData()
            refetchGroups({ placeId: place.id })
          })
          .catch((error) => {
            const code = error?.response?.data?.details?.code
            if (code === 610) {
              setFieldError(
                'name',
                formatMessage({ id: 'error.400.subgroupNameExists' }),
              )
            }
          })
          .finally(() => {
            setLoading(DIALOG_KEYS.SUB_GROUP_ADD, false)
          })
      },
    })
  }

  const handleOpenAddGroup = (event) => {
    if (groupCount >= GROUP_MAX) {
      openDialog(DIALOG_KEYS.GROUP_LIMIT, {
        description: formatMessage({
          id: 'groupv4.dialog.limit.description',
        }),
      })
    } else {
      onOpenAddGroupDialog(event)
    }
  }

  useEffect(() => {
    getGroupFirstTime({ placeId: place.id })
  }, [])

  useEffect(() => {
    if (isResetSelected) setIsResetSelected(false)
  }, [isResetSelected])

  return (
    <PanelPages
      isEmptyState={isEmptyGroups && !isLoading}
      emptyState={
        <EmptyState
          title={formatMessage({
            id: 'groupsv4.empty.title',
          })}
          description={
            <div className="w-512">
              <div>
                {formatMessage({
                  id: 'groupsv4.empty.description1',
                })}
              </div>
              {isOwner && (
                <div>
                  {formatMessage({
                    id: 'groupsv4.empty.description2',
                  })}
                </div>
              )}
            </div>
          }
          icon={<img src={Assets.groupv4EmptyIcon} alt="Group empty icon" />}
          accesses={[AccessType.SUBSCRIBER_CREATE, AccessType.ADMINISTRATION]}
          onAction={
            <div className="space-y-10">
              <AccessControl accesses={[AccessType.ADMINISTRATION]}>
                <OutlinedButtons
                  id="open-create-group-dialog-empty"
                  color={SECONDARY}
                  onClick={handleOpenAddGroup}
                  className="w-full capitalize"
                >
                  <div className="flex">
                    <GroupIcon classes="w-22 mr-3" color="#00B2AD" />
                    {formatMessage({
                      id: 'create.group.newGroup',
                    })}
                  </div>
                </OutlinedButtons>
              </AccessControl>
            </div>
          }
        />
      }
      title={formatMessage({
        id: 'groups.header.label',
      })}
      rightContent={
        <div className="space-y-5 md:space-y-0 md:space-x-10 md:flex items-center">
          <FormControl variant="outlined" className="w-full md:w-256">
            <SimpleInput
              id="search-group-page"
              name="search"
              placeholder={formatMessage({
                id: 'groupv4.search.placeholder',
              })}
              fullWidth
              size="small"
              onChange={(event) => {
                event.stopPropagation()
                searchGroups({
                  placeId: place.id,
                  keyword: event.target.value,
                  onClearData,
                })
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon className="text-grey-200" />
                </InputAdornment>
              }
            />
          </FormControl>
          <div className="w-full md:w-fit">
            <AccessControl accesses={[AccessType.ADMINISTRATION]}>
              <OutlinedButtons
                id="open-create-group-dialog"
                color={SECONDARY}
                onClick={handleOpenAddGroup}
                className="w-full capitalize"
              >
                <div className="flex">
                  <GroupIcon classes="w-22 mr-3" color="#00B2AD" />
                  {formatMessage({
                    id: 'create.group.newGroup',
                  })}
                </div>
              </OutlinedButtons>
            </AccessControl>
          </div>
          <AccessControl
            accesses={[AccessType.NOTIFICATION_SEND, AccessType.ADMINISTRATION]}
          >
            <ComposeMessageButton
              isLoading={isComposeLoading}
              onClick={async () => {
                setIsComposeLoading(true)
                const groupIds = Object.values(selectedIds).flat()
                const tagIds = !isEmptyOrNil(groupIds)
                  ? await getTagIds({ groupIds: groupIds.join(',') })
                  : []
                setIsComposeLoading(false)
                openCompose(tagIds)
              }}
            />
          </AccessControl>
        </div>
      }
    >
      <DataTable
        data={groups}
        isCheckbox={isOwner}
        className={clsx(isDownXs ? classes.grouptable : classes.table)}
        columns={ColumnOfData(
          formatMessage,
          isOwner,
          onOpenDeleteGroupDialog,
          openEditNameGroupDialog,
          openAddSubgroupDialog,
        )}
        pagination={meta}
        sticky="checkBox"
        isLoading={isLoading}
        isResetAllSelected={isResetSelected}
        onFetch={(value) => {
          refetchGroups(
            mergeLeft(
              { ...value, placeId: place.id, keyword: searchText },
              params,
            ),
          )
        }}
        onSelectedRow={(data) => {
          goToGroupDetail({ groupId: data.id })
        }}
        onChange={(value) => setSelectedIds(value)}
        bulkAction={
          isOwner ? (
            <div className="divide-x divide-neutrals-400 flex">
              <div className="pl-5">
                <Typography
                  className="text-14 font-400 text-red-500 cursor-pointer"
                  onClick={() =>
                    onOpenDeleteGroupDialog(
                      selectedIds[meta.currentPage],
                      'selected',
                    )
                  }
                >
                  {`${formatMessage({
                    id: 'conversation.message.delete',
                  })} (${selectedIds[meta.currentPage]?.length})`}
                </Typography>
              </div>
            </div>
          ) : null
        }
      />
    </PanelPages>
  )
}
